/**
 * Notification Center side drawer: lists unread OncoEMR tasks from the inbox poller.
 *
 * @module tasksNotificationCenterDrawer
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import SideModal from "../../../../../components/modals/filterModal/filterModal";
import ToolTipUsingPortal from "../../../../../components/toolTipUsingPortal/toolTipUsingPortal";
import type { TasksNotificationCenterItem } from "../../../../../context/tasksNotification/tasksNotificationTypes";
import { CopyIcon } from "../../../../../svg/copy-icon";
import { CrossIcon } from "../../../../../svg/cross-icon";
import { TrashIcon } from "../../../../../svg/trashIcon";

export type { TasksNotificationCenterItem } from "../../../../../context/tasksNotification/tasksNotificationTypes";

/**
 * Props for {@link TasksNotificationCenterDrawer}.
 */
export interface TasksNotificationCenterDrawerProps {
  items: TasksNotificationCenterItem[];
  isOpen: boolean;
  onClose: () => void;
  dismissItems: (ids: string[]) => void;
  loading?: boolean;
}

/** How long the copy confirmation tooltip stays visible (ms). */
const COPY_FEEDBACK_DURATION_MS = 1500;

/**
 * Copy button for patient MRN with a brief portaled tooltip (avoids toast z-index issues).
 *
 * @param props - Component props.
 * @param props.mrn - MRN string written to the clipboard on click.
 * @returns Copy control with transient "Copied" feedback above the icon.
 */
const NotificationMrnCopyButton = ({ mrn }: { mrn: string }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showCopied, setShowCopied] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    },
    [],
  );

  const handleCopy = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!mrn) {
        return;
      }
      navigator.clipboard.writeText(mrn);
      setShowCopied(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = setTimeout(
        () => setShowCopied(false),
        COPY_FEEDBACK_DURATION_MS,
      );
    },
    [mrn],
  );

  return (
    <div ref={anchorRef} className="relative shrink-0">
      <button
        type="button"
        className="flex cursor-pointer border-0 bg-transparent p-0"
        onClick={handleCopy}
        aria-label="Copy patient MRN"
      >
        <CopyIcon height="12" width="12" />
      </button>
      <ToolTipUsingPortal
        placement="top"
        showTooltip={showCopied}
        parentRef={anchorRef}
        offsetPx={6}
        maxWidth="4.5rem"
      >
        <span className="block text-[10px] font-medium leading-3 text-[#0F0F0F]">
          Copied
        </span>
      </ToolTipUsingPortal>
    </div>
  );
};

const TasksNotificationCenterDrawer = ({
  items,
  isOpen,
  onClose,
  dismissItems,
  loading = false,
}: TasksNotificationCenterDrawerProps) => {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setSelectionMode(false);
      setSelectedIds({});
    }
  }, [isOpen]);

  const selectedCount = useMemo(
    () => Object.values(selectedIds).filter(Boolean).length,
    [selectedIds],
  );

  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedIds({});
      }
      return !prev;
    });
  }, []);

  const handleToggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleRemoveSelected = useCallback(() => {
    const idsToRemove = Object.entries(selectedIds)
      .filter(([, checked]) => checked)
      .map(([id]) => id);
    dismissItems(idsToRemove);
    setSelectedIds({});
    setSelectionMode(false);
  }, [dismissItems, selectedIds]);

  return (
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      id="tasks-notification-center"
      containerClassName="!z-[10000]"
    >
      <div className="flex h-full flex-col">
        <header className="flex items-center justify-between gap-[0.625rem] border-b border-primaryGray-14 px-5 py-3">
          <h2 className="m-0 text-lg font-bold leading-[1.625rem] text-[#0F0F0F]">
            Notification Center
          </h2>
          <div className="flex items-center gap-2">
            {selectionMode ? (
              <button
                type="button"
                className="rounded bg-[#0F0F0F] px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                onClick={handleRemoveSelected}
                disabled={selectedCount === 0}
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center"
                onClick={handleToggleSelectionMode}
                aria-label="Select notifications to remove"
                aria-pressed={selectionMode}
              >
                <TrashIcon width={18} height={18} />
              </button>
            )}
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded border border-transparent"
              onClick={onClose}
              aria-label="Close notification center"
            >
              <CrossIcon fillColor="#0F0F0F" width="20" height="20" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && items.length === 0 ? (
            <p className="text-sm text-primaryGray-7">Loading notifications…</p>
          ) : null}
          {!loading && items.length === 0 ? (
            <p className="text-sm text-primaryGray-7">
              No notifications to show.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-6 p-0">
              {items.map((row) => (
                <li key={row.id} className="flex items-stretch gap-[0.625rem]">
                  {selectionMode ? (
                    <label className="flex cursor-pointer items-center pt-1">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-primaryGray-12 text-primaryGray-1"
                        checked={Boolean(selectedIds[row.id])}
                        onChange={() => handleToggleRow(row.id)}
                        aria-label={`Select ${row.title}`}
                      />
                    </label>
                  ) : null}
                  <div
                    className={`min-w-0 flex-1 rounded-md border border-primaryGray-14 bg-white px-4 py-3 shadow-sm ${
                      selectedIds[row.id] ? "bg-primaryGray-15" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-5">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm font-bold leading-[1.375rem] text-[#0F0F0F]">
                          {row.title}
                        </span>
                      </div>
                      <span className="text-xs font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-3">
                        {row.timestampLabel}
                      </span>
                    </div>
                    <div className="mt-2 flex items-start justify-between gap-3 text-xs font-normal text-primaryGray-3">
                      <div className="flex min-w-0 flex-1 gap-2 text-tiny leading-[1.125rem] tracking-[0.0125rem]">
                        <div className="m-0 flex min-w-0 items-center gap-1.5">
                          <p className="m-0 truncate">Patient MRN: {row.mrn}</p>
                          {row.mrn ? (
                            <NotificationMrnCopyButton mrn={row.mrn} />
                          ) : null}
                        </div>
                        <p>|</p>
                        <p className="m-0">Sender: {row.senderName}</p>
                      </div>
                      <Tags
                        label="Tasks"
                        backgroundColor="#FEF9C3"
                        textColor="#854D0E"
                        className="shrink-0 self-center"
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default TasksNotificationCenterDrawer;
